import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { icon, label } from "../../../../config";
import { downloadFile } from "../../../TaskSidebar/TaskSidebarContent/Attachments/attachments.helper";
import { useFormCalculation } from "../../contexts/FormCalculationContext";
import { TABLE_RENDERER } from "../../FormsConstant";
import TableFileUploadModal from "./TableFileUploadModal";
import "./TableRenderer.scss";
import { getCalculationFunction, getOperationDisplayName } from "./tableCalculations";

/**
 * Default pixel width per table column when none is configured.
 * @param {number} count
 * @returns {number[]}
 * @author Abhishek Dhillon
 */
const buildDefaultColumnWidths = (count) => Array.from({ length: count }, () => TABLE_RENDERER.COLUMN_DEFAULT_WIDTH_PX);

/**
 * Resolves column widths from stored properties, current state, or defaults.
 * @param {number} columnCount
 * @param {number[]|undefined} storedWidths
 * @param {number[]|undefined} fallbackWidths
 * @returns {number[]}
 * @author Abhishek Dhillon
 */
const resolveColumnWidths = (columnCount, storedWidths, fallbackWidths) => {
  if (columnCount === 0) return [];
  if (storedWidths?.length === columnCount) return [...storedWidths];
  if (fallbackWidths?.length === columnCount) return [...fallbackWidths];
  return buildDefaultColumnWidths(columnCount);
};

const TableRenderer = ({ element, onChange, isInteractive = false, showResponses = false, attachments }) => {
  const columns = element.properties?.columns || [];
  const { value } = element;
  const showDeleteColumn = isInteractive || !showResponses;

  // Get form context for storing calculations efficiently
  const { updateTableCalculations } = useFormCalculation();

  const [tableData, setTableData] = useState(value?.length > 0 ? value : [{}]);
  const [modalState, setModalState] = useState({ open: false, rowIndex: null, columnIndex: null });
  const [columnWidths, setColumnWidths] = useState(() =>
    resolveColumnWidths(columns.length, element.properties?.columnWidths)
  );
  const [resizingColumnIndex, setResizingColumnIndex] = useState(null);

  // Sync with external value changes
  useEffect(() => {
    if (value && value.length > 0) {
      setTableData(value);
    }
  }, [value]);

  // Keep column widths aligned when column count or stored widths change
  useEffect(() => {
    setColumnWidths((prev) => resolveColumnWidths(columns.length, element.properties?.columnWidths, prev));
  }, [columns.length, element.properties?.columnWidths]);

  /**
   * Starts a drag resize for the column at `columnIndex` (TableRenderer).
   * @param {number} columnIndex
   * @param {number} clientX
   * @author Abhishek Dhillon
   */
  const handleColumnResizeStart = useCallback(
    (columnIndex, clientX) => {
      const startX = clientX;
      const startWidth = columnWidths[columnIndex] ?? TABLE_RENDERER.COLUMN_DEFAULT_WIDTH_PX;

      setResizingColumnIndex(columnIndex);
      document.body.classList.add("table-renderer--resizing");

      const handleMouseMove = (moveEvent) => {
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.max(TABLE_RENDERER.COLUMN_MIN_WIDTH_PX, startWidth + delta);
        setColumnWidths((prev) => {
          const next = [...prev];
          next[columnIndex] = newWidth;
          return next;
        });
      };

      const handleMouseUp = () => {
        setResizingColumnIndex(null);
        document.body.classList.remove("table-renderer--resizing");
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [columnWidths]
  );

  /** Renders `<colgroup>` so header, body, and calculation rows share column widths (TableRenderer). */
  const renderColGroup = () => (
    <colgroup>
      {columns.map((_, columnIndex) => (
        <col key={columnIndex} style={{ width: `${columnWidths[columnIndex]}px` }} />
      ))}
      {showDeleteColumn && <col style={{ width: `${TABLE_RENDERER.DELETE_ROW_ACTION_WIDTH_PX}px` }} />}
    </colgroup>
  );

  // Initialize date cells with current date if they're empty (only when interactive)
  useEffect(() => {
    if (isInteractive && tableData.length > 0 && columns.length > 0) {
      let hasChanges = false;
      const newTableData = tableData.map((row) => {
        const newRow = { ...row };
        columns.forEach((column, columnIndex) => {
          if (column.type === "date" && !newRow[`col_${columnIndex}`]) {
            newRow[`col_${columnIndex}`] = new Date().toISOString().split("T")[0];
            hasChanges = true;
          }
        });
        return newRow;
      });

      if (hasChanges) {
        setTableData(newTableData);
        if (onChange) {
          onChange(newTableData);
        }
      }
    }
    
  }, []); 

  const handleAddRow = () => {
    const newRow = {};
    columns.forEach((column, index) => {
      // Set appropriate default value based on column type
      if (column.type === "dropdown") {
        newRow[`col_${index}`] = ""; // Empty selection for dropdown
      } else if (column.type === "date") {
        newRow[`col_${index}`] = new Date().toISOString().split("T")[0]; // Today's date
      } else {
        newRow[`col_${index}`] = ""; // Empty string for text/number
      }
    });
    const newTableData = [...tableData, newRow];
    setTableData(newTableData);

    // Notify parent of changes if interactive
    if (isInteractive && onChange) {
      onChange(newTableData);
    }
  };

  const handleCellChange = (rowIndex, columnIndex, cellValue) => {
    const newTableData = [...tableData];
    if (!newTableData[rowIndex]) {
      newTableData[rowIndex] = {};
    }
    newTableData[rowIndex][`col_${columnIndex}`] = cellValue;
    setTableData(newTableData);

    // Notify parent of changes if interactive
    if (isInteractive && onChange) {
      onChange(newTableData);
    }
  };

  const handleDeleteRow = (rowIndex) => {
    // Don't allow deleting if there's only one row
    if (tableData.length <= 1) return;

    const newTableData = tableData.filter((_, index) => index !== rowIndex);
    setTableData(newTableData);

    // Notify parent of changes if interactive
    if (isInteractive && onChange) {
      onChange(newTableData);
    }
  };

  const handleFileUploadSelect = (file, autoRemove) => {
    const cellValue = autoRemove && file?.source === "cms" ? { ...file, pendingDelete: true } : file;
    handleCellChange(modalState.rowIndex, modalState.columnIndex, cellValue);
    setModalState({ open: false, rowIndex: null, columnIndex: null });
  };

  /** Read-only view of a fileUpload cell shown when showResponses=true */
  const renderFileUploadReadOnly = (cellValue, rowIndex, columnIndex) => {
    if (!cellValue || typeof cellValue !== "object" || !cellValue.name) {
      return <span className="text-muted" style={{ fontSize: "12px" }}>No file</span>;
    }

    const { name, source, attachmentKey } = cellValue;
    const compositeKey = `${element.id}__${rowIndex}__${columnIndex}`;
    const backendFile = source === "device" ? attachments?.[compositeKey]?.[0] : null;
    const hasUrl = source === "cms" && attachmentKey;
    const hasBackendUrl = backendFile?.url;

    return (
      <div className="table-file-upload-cell">
        <span className="table-file-upload-cell__file-icon">{icon.FORM_ICON}</span>
        {hasUrl && (
          <button
            className="table-file-upload-cell__download"
            style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }}
            onClick={() => downloadFile(null, name, attachmentKey)}
            title={name}>
            {name}
          </button>
        )}
        {hasBackendUrl && (
          <a href={backendFile.url} target="_blank" rel="noopener noreferrer" className="table-file-upload-cell__download" title={name}>
            {backendFile.filename || name}
          </a>
        )}
        {!hasUrl && !hasBackendUrl && (
          <span className="table-file-upload-cell__file-name" title={name}>{name}</span>
        )}
      </div>
    );
  };

  /** Editable view of a fileUpload cell shown during form filling */
  const renderFileUploadEditable = (cellValue, rowIndex, columnIndex, isDisabled) => {
    const hasFile = cellValue && typeof cellValue === "object" && cellValue.name;

    return (
      <div className="table-file-upload-cell">
        {hasFile ? (
          <>
            <div className="table-file-upload-cell__file">
              <span className="table-file-upload-cell__file-icon">{icon.FORM_ICON}</span>
              <span className="table-file-upload-cell__file-name" title={cellValue.name}>{cellValue.name}</span>
            </div>
            {!isDisabled && (
              <div className="table-file-upload-cell__actions">
                <button
                  className="table-file-upload-cell__action-btn"
                  title="Change file"
                  type="button"
                  onClick={() => setModalState({ open: true, rowIndex, columnIndex })}>
                  {icon.EDIT_ICON}
                </button>
                <button
                  className="table-file-upload-cell__action-btn table-file-upload-cell__action-btn--delete"
                  title="Remove file"
                  type="button"
                  onClick={() => handleCellChange(rowIndex, columnIndex, "")}>
                  {icon.DELETE_ICON}
                </button>
              </div>
            )}
          </>
        ) : (
          <button
            className="table-file-upload-cell__btn"
            type="button"
            disabled={isDisabled}
            onClick={() => !isDisabled && setModalState({ open: true, rowIndex, columnIndex })}>
             {icon.UPLOAD}&nbsp; {label.UPLOAD_FILE}
          </button>
        )}
      </div>
    );
  };

  /** Renders a fileUpload column cell — delegates to read-only or editable view */
  const renderFileUploadCell = (columnIndex, row, rowIndex, isDisabled) => {
    const cellValue = row[`col_${columnIndex}`] || "";
    if (showResponses) return renderFileUploadReadOnly(cellValue, rowIndex, columnIndex);
    return renderFileUploadEditable(cellValue, rowIndex, columnIndex, isDisabled);
  };

  /** Renders a dropdown column cell */
  const renderDropdownCell = (column, columnIndex, row, rowIndex, isDisabled) => {
    const cellValue = row[`col_${columnIndex}`] || "";
    const options = column.options || [];
    const selectedOption = options.find((opt) => opt.value === cellValue) || null;

    return (
      <DropDownList
        data={options}
        textField='value'
        dataItemKey='id'
        value={selectedOption}
        onChange={(e) => handleCellChange(rowIndex, columnIndex, e.target.value?.value || "")}
        disabled={isDisabled}
        placeholder='Select option...'
        className='w-100'
        style={{ minWidth: "120px" }}
      />
    );
  };

  /** Renders text / number / date column cells */
  const renderDefaultCell = (column, columnIndex, row, rowIndex, isDisabled) => {
    const cellValue = row[`col_${columnIndex}`] || "";
    return (
      <Input
        value={cellValue}
        onChange={(e) => handleCellChange(rowIndex, columnIndex, e.target.value)}
        disabled={isDisabled}
        type={column?.type || "text"}
        className='w-100'
      />
    );
  };

  /** Dispatches to the correct cell renderer based on column type */
  const renderCellInput = (column, columnIndex, row, rowIndex) => {
    const isDisabled = showResponses || !isInteractive;

    if (column.type === "fileUpload") return renderFileUploadCell(columnIndex, row, rowIndex, isDisabled);
    if (column.type === "dropdown")   return renderDropdownCell(column, columnIndex, row, rowIndex, isDisabled);
    return renderDefaultCell(column, columnIndex, row, rowIndex, isDisabled);
  };

  // Calculate results for number columns with showResult enabled
  const columnCalculations = useMemo(() => {
    const calculations = {};

    columns.forEach((column, columnIndex) => {
      if (column.type === "number" && column.showResult) {
        const operation = column.operation || "addition";
        const calculateFn = getCalculationFunction(operation);
        const calculatedValue = calculateFn(tableData, columnIndex);

        calculations[columnIndex] = {
          value: calculatedValue,
          operation,
          displayName: getOperationDisplayName(operation),
          linkedVariableId: column.linkedSmartChipId || null,
        };
      }
    });

    return calculations;
  }, [tableData, columns]);

  // Store calculations in FormContext for efficient form submission
  useEffect(() => {
    if (updateTableCalculations && element.id) {
      // Only store calculations that are linked to workflow variables
      const linkedCalculations = {};
      Object.entries(columnCalculations).forEach(([columnIndex, calc]) => {
        if (calc.linkedVariableId) {
          linkedCalculations[columnIndex] = calc;
        }
      });

      // Update context with calculations
      updateTableCalculations(element.id, linkedCalculations);
    }
  }, [columnCalculations, updateTableCalculations, element.id]);

  return (
    <div className='table-renderer overflow-x-auto overflow-y-hidden w-100' id={`table-renderer-${element.id}`}>
      <table className='table border mb-1 table-renderer__table'>
        {renderColGroup()}
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th key={index} className='border table-renderer__header-cell'>
                <span className='table-renderer__header-title'>{column.title}</span>
                <span
                  className={`table-renderer__resize-handle${resizingColumnIndex === index ? " table-renderer__resize-handle--active" : ""}`}
                  role='separator'
                  aria-orientation='vertical'
                  title={TABLE_RENDERER.COLUMN_RESIZE_TITLE}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleColumnResizeStart(index, e.clientX);
                  }}
                />
              </th>
            ))}
            {showDeleteColumn && <th className='border' aria-hidden='true' />}
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column, columnIndex) => (
                <td key={columnIndex} className='border table-renderer__cell'>
                  {renderCellInput(column, columnIndex, row, rowIndex)}
                </td>
              ))}
              {showDeleteColumn && (
                <td className='border text-center table-renderer__cell'>
                  <span
                    id={`table-renderer-delete-row-${element.id}-${rowIndex}`}
                    className='btn btn-link text-danger p-0'
                    onClick={() => handleDeleteRow(rowIndex)}
                    disabled={showResponses || tableData.length <= 1}
                    title={TABLE_RENDERER.DELETE_ROW_TITLE}
                    style={{ fontSize: "18px" }}>
                    ✕
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {showDeleteColumn && (
        <div className='d-flex justify-content-end'>
          <button
            id={`table-renderer-add-row-${element.id}`}
            type='button'
            className='btn add-row-btn'
            onClick={handleAddRow}
            disabled={showResponses}>
            {TABLE_RENDERER.ADD_ROW_LABEL}
          </button>
        </div>
      )}

      {/* Calculation Result Boxes */}
      {Object.keys(columnCalculations).length > 0 && (
        <table className='table table-renderer__table'>
          {renderColGroup()}
          <tbody>
            <tr>
              {columns.map((column, columnIndex) => (
                <td key={columnIndex} className='border-0 pt-0 table-renderer__cell'>
                  {columnCalculations[columnIndex] && (
                    <div className='calculation-result-container'>
                      <span className='calculation-label text-muted d-block mb-1'>{columnCalculations[columnIndex].displayName}</span>
                      <Input value={columnCalculations[columnIndex].value} readOnly className='calculation-input text-start' />
                    </div>
                  )}
                </td>
              ))}
              {showDeleteColumn && <td className='border-0' aria-hidden='true' />}
            </tr>
          </tbody>
        </table>
      )}

      {/* File upload modal */}
      {modalState.open && (
        <TableFileUploadModal
          onSelect={handleFileUploadSelect}
          multiple={false}
          onClose={() => setModalState({ open: false, rowIndex: null, columnIndex: null })}
        />
      )}
    </div>
  );
};

export default TableRenderer;
